import type {
  CaptureServicePaperReturnInput,
  PlanServiceJobInput,
  ServiceExecutionMode,
  ServiceListBucket,
  ServiceSearchInput,
  ServiceStatus,
} from './types';

const fieldTransitions: Readonly<Record<ServiceStatus, readonly ServiceStatus[]>> = {
  new: ['planned', 'billing-ready', 'cancelled'],
  planned: ['printed', 'billing-ready', 'cancelled'],
  printed: ['issued', 'planned', 'billing-ready', 'cancelled'],
  issued: ['paper-returned', 'printed', 'billing-ready', 'cancelled'],
  'paper-returned': ['reviewed', 'issued', 'billing-ready'],
  reviewed: ['billing-ready', 'planned'],
  'billing-ready': ['completed', 'reviewed'],
  completed: [],
  cancelled: [],
  'pickup-planned': [],
  received: [],
  diagnosis: [],
  'estimate-ready': [],
  'approval-pending': [],
  approved: [],
  declined: [],
  repairing: [],
  'return-planned': [],
  returned: [],
};

const workshopTransitions: Readonly<Record<ServiceStatus, readonly ServiceStatus[]>> = {
  new: ['pickup-planned', 'received', 'cancelled'],
  'pickup-planned': ['received', 'cancelled'],
  received: ['diagnosis', 'cancelled'],
  diagnosis: ['estimate-ready', 'cancelled'],
  'estimate-ready': ['approval-pending', 'diagnosis'],
  'approval-pending': ['approved', 'declined', 'estimate-ready'],
  approved: ['repairing', 'return-planned'],
  declined: ['return-planned'],
  repairing: ['return-planned', 'diagnosis'],
  'return-planned': ['returned'],
  returned: ['billing-ready'],
  'billing-ready': ['completed', 'returned'],
  completed: [],
  cancelled: [],
  planned: [],
  printed: [],
  issued: [],
  'paper-returned': [],
  reviewed: [],
};

export function serviceStatusBucket(status: ServiceStatus): ServiceListBucket {
  if (['new', 'planned', 'pickup-planned'].includes(status)) return 'planning';
  if (['printed', 'issued'].includes(status)) return 'issued';
  if (['paper-returned', 'returned'].includes(status)) return 'returned';
  if (status === 'billing-ready') return 'billing';
  if (['completed', 'cancelled'].includes(status)) return 'completed';
  return 'open';
}

export function allowedServiceTransitions(
  executionMode: ServiceExecutionMode,
  status: ServiceStatus,
): readonly ServiceStatus[] {
  return executionMode === 'field'
    ? fieldTransitions[status]
    : workshopTransitions[status];
}

export function assertServiceTransition(
  executionMode: ServiceExecutionMode,
  fromStatus: ServiceStatus,
  toStatus: ServiceStatus,
): void {
  if (!allowedServiceTransitions(executionMode, fromStatus).includes(toStatus)) {
    throw new Error(`Statuswechsel von ${fromStatus} nach ${toStatus} ist für ${executionMode} nicht zulässig.`);
  }
}

export function normalizeServiceSearchInput(input: ServiceSearchInput): Required<ServiceSearchInput> {
  return {
    tenantId: input.tenantId.trim(),
    query: input.query?.trim() ?? '',
    bucket: input.bucket ?? 'open',
    executionMode: input.executionMode ?? 'all',
    kind: input.kind ?? 'all',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(200, Math.max(1, Math.trunc(input.pageSize ?? 50))),
  };
}

export function normalizePlanServiceJobInput(input: PlanServiceJobInput): PlanServiceJobInput {
  if (!input.tenantId.trim()) throw new Error('Mandant fehlt.');
  if (!input.orderId.trim()) throw new Error('Der Serviceauftrag benötigt einen Orders-Vorgang.');
  if (!input.workDescription.trim()) throw new Error('Bitte eine Arbeits- oder Fehlerbeschreibung erfassen.');
  if (input.scheduledStart && input.scheduledEnd && input.scheduledEnd < input.scheduledStart) {
    throw new Error('Das geplante Ende darf nicht vor dem geplanten Beginn liegen.');
  }
  return {
    ...input,
    tenantId: input.tenantId.trim(),
    orderId: input.orderId.trim(),
    serviceJobId: input.serviceJobId?.trim() || undefined,
    technicianName: input.technicianName.trim(),
    workDescription: input.workDescription.trim(),
    internalNotes: input.internalNotes?.trim() ?? '',
    serviceAddress: {
      companyName: input.serviceAddress.companyName.trim(),
      contactName: input.serviceAddress.contactName.trim(),
      street: input.serviceAddress.street.trim(),
      postalCode: input.serviceAddress.postalCode.trim(),
      city: input.serviceAddress.city.trim(),
      countryCode: input.serviceAddress.countryCode.trim().toUpperCase() || 'DE',
    },
  };
}

export function normalizePaperReturnInput(
  input: CaptureServicePaperReturnInput,
): CaptureServicePaperReturnInput {
  if (!input.workPerformed.trim()) throw new Error('Die Rückmeldung benötigt eine Tätigkeitsbeschreibung.');
  if (input.workHours != null && input.workHours < 0) throw new Error('Arbeitszeit darf nicht negativ sein.');
  if (input.travelHours != null && input.travelHours < 0) throw new Error('Fahrtzeit darf nicht negativ sein.');
  if (input.kilometers != null && input.kilometers < 0) throw new Error('Kilometer dürfen nicht negativ sein.');
  return {
    ...input,
    technicianName: input.technicianName.trim(),
    arrivalTime: input.arrivalTime?.trim() ?? '',
    departureTime: input.departureTime?.trim() ?? '',
    travelHours: input.travelHours ?? 0,
    workHours: input.workHours ?? 0,
    kilometers: input.kilometers ?? 0,
    workPerformed: input.workPerformed.trim(),
    materials: (input.materials ?? []).map((material) => ({
      articleNumber: material.articleNumber.trim(),
      description: material.description.trim(),
      quantity: Math.max(0, material.quantity),
      unit: material.unit.trim() || 'Stück',
    })).filter((material) => material.description),
    followUpNotes: input.followUpNotes?.trim() ?? '',
    customerName: input.customerName?.trim() ?? '',
    officeNotes: input.officeNotes?.trim() ?? '',
  };
}
